import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  Grid,
  Container,
  Divider
} from '@mui/material';
import {
  FileUpload,
  Update,
  Download,
  Add,
  Inventory
} from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import APIURL from '../api/apiClient';
import { message } from 'antd';

// Inline styles instead of styled components to avoid issues
const cardStyle = {
  height: '100%',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 8
  }
};

const actionButtonStyle = {
  marginTop: 2,
  padding: 1.5,
  width: '100%'
};

const noteBoxStyle = {
  backgroundColor: 'info.light',
  borderLeft: '4px solid',
  borderLeftColor: 'info.main',
  padding: 2,
  marginTop: 2,
  borderRadius: 1
};

const ItemActions = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const newDataRaw = await APIURL.get("/fetchcategories.php");

        // Make sure it's an array
        const newData = Array.isArray(newDataRaw) ? newDataRaw : JSON.parse(newDataRaw);

        const formattedCategories = newData.map((category) => ({
          value: category.id,
          label: category.name,
        }));

        setCategories(formattedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchData();
  }, []);

  const handleDownloadAll = () => {
    // API endpoint of your PHP script
    const url = `${APIURL.baseURL.replace('/api', '')}/import_excel/downloaditems.php`;

    // Trigger browser download
    window.open(url, "_blank");
  };

 const handleDownloadByCategory = () => {
  if (!selectedCategory) {
    message.warning("Please select a category first.");
    return;
  }

  const url = `${APIURL.baseURL.replace('/api', '')}/import_excel/downloaditems.php?category_id=${selectedCategory}`;
  window.open(url, "_blank");
};

  const handleBulkUpload = () => {
    navigate('/excelimport')
    // Navigation logic here
  };

  const handleUpdateItems = () => {
    navigate('/updateitemscsv')
    // Navigation logic here
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
          color: 'white',
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        <Inventory sx={{ fontSize: 30, mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom>
          Item Management Dashboard
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Bulk Upload for New Items */}
        <Grid item xs={12} md={4}>
          <Card sx={cardStyle}>
            <CardHeader
              avatar={<FileUpload color="primary" />}
              title={
                <Typography variant="h6" component="h2">
                  Items Bulk Upload
                </Typography>
              }
              subheader="Add new items to inventory"
            />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Use this section to import new items in bulk using Excel files.
                This will create new inventory records.
              </Typography>

              <Button
                variant="contained"
                startIcon={<Add />}
                sx={actionButtonStyle}
                onClick={handleBulkUpload}
              >
                Import Excel for New Items
              </Button>

              <Box sx={noteBoxStyle}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Note: This is for bulk upload of new items. The Excel file should
                  follow the required template format with item details.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Update Existing Items */}
        <Grid item xs={12} md={4}>
          <Card sx={cardStyle}>
            <CardHeader
              avatar={<Update color="secondary" />}
              title={
                <Typography variant="h6" component="h2">
                  Update Existing Items
                </Typography>
              }
              subheader="Modify current inventory"
            />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Update pricing, quantities, and details of existing items in
                your inventory using CSV files.
              </Typography>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<FileUpload />}
                sx={actionButtonStyle}
                onClick={handleUpdateItems}
              >
                Update Items via CSV
              </Button>

              <Box sx={{ ...noteBoxStyle, borderLeftColor: 'secondary.main' }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Note: This functionality is for updating existing items only.
                  Make sure your CSV includes item IDs for proper matching.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Download CSV */}
        <Grid item xs={12} md={4}>
          <Card sx={cardStyle}>
            <CardHeader
              avatar={<Download color="success" />}
              title={
                <Typography variant="h6" component="h2">
                  Download Items Data
                </Typography>
              }
              subheader="Export inventory data"
            />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Download your inventory data in CSV format for reporting,
                analysis, or backup purposes.
              </Typography>

              {/* Download All Items */}
              <Button
                variant="contained"
                color="success"
                startIcon={<Download />}
                sx={actionButtonStyle}
                onClick={handleDownloadAll}
              >
                Download All Items
              </Button>

              <Divider sx={{ my: 2 }}>OR</Divider>

              {/* Download by Category */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="category-select-label">Select Category *</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={selectedCategory}
                  label="Select Category *"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="success"
                startIcon={<Download />}
                sx={actionButtonStyle}
                onClick={handleDownloadByCategory}
                disabled={!selectedCategory}
              >
                Download by Category
              </Button>

              <Alert severity="info" sx={{ mt: 2 }}>
                Select a category to download specific inventory data
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Information */}
      {/* <Paper sx={{ p: 3, mt: 4, backgroundColor: 'background.default' }}>
        <Typography variant="h6" gutterBottom>
          Instructions
        </Typography>
        <Typography variant="body2" paragraph>
          • Use "Items Bulk Upload" to add new items to your inventory via Excel files
        </Typography>
        <Typography variant="body2" paragraph>
          • Use "Update Existing Items" to modify current inventory items via CSV files
        </Typography>
        <Typography variant="body2" paragraph>
          • Download complete inventory data or filter by specific categories
        </Typography>
        <Typography variant="body2">
          • Ensure all files follow the required template format for successful processing
        </Typography>
      </Paper> */}
    </Container>
  );
};

export default ItemActions;